import { useMemo } from "react";
import { ApolloError, useQuery } from "@apollo/client";
import {
  ticketEndpointContextEnum,
  ticketEndpointTypeEnum,
} from "@SeemplicityDev/ticket-master";
import { GET_AUTOMATION } from "../../AutomationsAPI";
import { validateAutomationData } from "../../AutomationsUtils";
import useAutomationURLData, {
  AutomationURLDataReturn,
} from "../useAutomationURLData/useAutomationURLData";
import useTicketManagers from "../../../Ticketing/useTicketManagers/useTicketManagers";
import {
  ViewsWizardFormFields,
  parseViewsInputParams,
  validateViewsInputParams,
} from "./viewsWizardUtils";

interface UseTicketManagersResult {
  ticketManagers: { providers?: { id?: string }[] }[] | null;
  loading: boolean;
  error?: ApolloError;
}

export const useViewsWizardData = (id?: string) => {
  const automationURLData = useAutomationURLData();

  const {
    data,
    loading,
    error: automationError,
  } = useQuery(GET_AUTOMATION, {
    variables: { id },
    skip: !id,
  });

  const {
    ticketManagers,
    loading: providersLoading,
    error: providersError,
  } = useTicketManagers({
    context: ticketEndpointContextEnum.RMQ,
    type: ticketEndpointTypeEnum.INTERNAL,
  }) as UseTicketManagersResult;

  const internalProviderId = useMemo(
    () =>
      ticketManagers?.find((tm) => !!tm.providers?.length)?.providers?.[0]?.id,
    [ticketManagers]
  );

  const automationData = useMemo(() => {
    const automation = data?.get_automation;
    const params = parseViewsInputParams(automation?.action?.params);

    if (
      !!automation &&
      !!params &&
      validateAutomationData(automation, validateViewsInputParams)
    ) {
      return { automation, params };
    }
  }, [data]);

  const wizardData:
    | ViewsWizardFormFields
    | AutomationURLDataReturn
    | undefined = useMemo(() => {
    if (!!automationData) {
      const { automation } = automationData;

      return {
        filter: { value: { id: automation.selection_rule?.filtersid } },
        scope: {
          value: {
            id:
              automation.selection_rule?.scopesid ||
              automation.selection_rule?.scope_group,
          },
        },
        name: automation.name,
      };
    } else if (!!automationURLData) {
      return automationURLData;
    }
  }, [automationData, automationURLData]);

  return {
    wizardData,
    internalProviderId,
    notFound: !!id && !automationData && !loading,
    providersLoading,
    error: automationError || providersError,
  };
};

export default useViewsWizardData;
